import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Avatar,
  Badge,
  BrandLogo,
  Button,
  Icon,
  Menu,
  Modal,
  ToastProvider,
  isIconName,
  type IconName,
} from "@evolua/ui";
import { useAuth } from "../auth/AuthContext.js";
import { buildLoginPath } from "../auth/auth-redirect.js";
import { useMeInboxUnreadCount } from "../api/use-inbox.js";
import { filterNavItems, NAV_GROUPS, NAV_ITEMS } from "../nav/nav-config.js";
import { InstallPrompt } from "../pwa/InstallPrompt.js";
import { OfflineBanner } from "../pwa/OfflineBanner.js";
import { SwUpdateBanner } from "../pwa/SwUpdateBanner.js";
import { ApiWakeBanner } from "../api/ApiWakeBanner.js";
import { hasPermission } from "../session/demo-session.js";
import { useSession } from "../session/SessionContext.js";
import type { DemoRole } from "../session/demo-session.js";
import { useEffect, useState } from "react";
import "./shell.css";

const ROLES: DemoRole[] = ["EMPLOYEE", "MANAGER", "HR"];
const COLLAPSE_KEY = "evolua.shell.collapsed";
const BOTTOM_SLOT_IDS = ["home", "payroll", "vacation", "timecard"] as const;
const HEADER_ONLY_IDS = new Set(["account", "notifications"]);

function asIcon(name: IconName | undefined): IconName {
  return name && isIconName(name) ? name : "home";
}

export function AppShell() {
  const { session, setRole, setCompanyId, isDemoMode } = useSession();
  const auth = useAuth();
  const navigate = useNavigate();
  const canInbox = Boolean(session && hasPermission(session, ["lgpd:export_own"]));
  const unreadQuery = useMeInboxUnreadCount(canInbox);
  const unread = unreadQuery.data?.unreadCount ?? 0;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  if (!session) {
    return (
      <div className="evolua-auth-boot" role="status">
        Preparando sessão…
      </div>
    );
  }

  const items = filterNavItems(NAV_ITEMS, session.permissions);
  const bottomItems = BOTTOM_SLOT_IDS.map((id) =>
    items.find((item) => item.id === id),
  ).filter((item): item is NonNullable<typeof item> => Boolean(item));
  const bottomIds = new Set(bottomItems.map((item) => item.id));
  const moreItems = items.filter(
    (item) => !bottomIds.has(item.id) && !HEADER_ONLY_IDS.has(item.id),
  );
  const moreGrouped = NAV_GROUPS.map((g) => ({
    ...g,
    items: moreItems.filter((i) => (i.group ?? "me") === g.id),
  })).filter((g) => g.items.length > 0);
  const grouped = NAV_GROUPS.map((g) => ({
    ...g,
    items: items.filter((i) => (i.group ?? "me") === g.id),
  })).filter((g) => g.items.length > 0);

  async function onLogout() {
    await auth.logout({ global: true, reason: "logout" });
    navigate(buildLoginPath({ reason: "logout" }), { replace: true });
  }

  const companyOptions = session.companies.map((c) => ({
    id: c.id,
    label: c.name,
    onSelect: () => setCompanyId(c.id),
  }));

  const roleLabel =
    session.role === "EMPLOYEE"
      ? "Colaborador"
      : session.role === "MANAGER"
        ? "Gestor"
        : "RH";

  return (
    <ToastProvider>
      <div
        className={collapsed ? "evolua-shell evolua-shell--collapsed" : "evolua-shell"}
      >
        <a className="evolua-skip" href="#conteudo-principal">
          Ir para o conteúdo
        </a>
        <OfflineBanner />
        <SwUpdateBanner />
        <ApiWakeBanner />
        <InstallPrompt />
        <header className="evolua-shell__header">
          <NavLink to="/" className="evolua-shell__brand">
            <BrandLogo size="header" />
          </NavLink>
          <Badge tone="neutral">{roleLabel}</Badge>
          {canInbox ? (
            <NavLink
              to="/notificacoes"
              className="evolua-shell__inbox"
              aria-label={
                unread > 0 ? `Notificações, ${unread} não lidas` : "Notificações"
              }
            >
              <Icon name="bell" size={18} />
              <span className="evolua-shell__inbox-label">Avisos</span>
              {unread > 0 ? <Badge tone="warning">{unread}</Badge> : null}
            </NavLink>
          ) : null}
          <div className="evolua-shell__header-actions">
            {session.companies.length > 0 ? (
              <Menu
                label="Empresa"
                trigger={
                  <span className="evolua-shell__company">
                    <Icon name="building" size={16} />
                    <span className="evolua-shell__company-name">
                      {session.companies.find((c) => c.id === session.companyId)?.name ??
                        "Empresa"}
                    </span>
                    <Icon name="chevron-down" size={14} />
                  </span>
                }
                items={companyOptions}
              />
            ) : null}
            <Menu
              label="Conta"
              trigger={
                <span className="evolua-shell__user">
                  <Avatar name={session.displayName} size="sm" />
                  <span className="evolua-shell__user-name">{session.displayName}</span>
                  <Icon name="chevron-down" size={14} />
                </span>
              }
              items={[
                { id: "account", label: "Conta", onSelect: () => navigate("/conta") },
                ...(isDemoMode && setRole
                  ? ROLES.map((role) => ({
                      id: `role-${role}`,
                      label:
                        role === "EMPLOYEE"
                          ? "Demo: Colaborador"
                          : role === "MANAGER"
                            ? "Demo: Gestor"
                            : "Demo: RH",
                      onSelect: () => setRole(role),
                    }))
                  : [
                      {
                        id: "logout",
                        label: "Sair",
                        onSelect: () => void onLogout(),
                        danger: true,
                      },
                    ]),
              ]}
            />
            {!isDemoMode ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void onLogout()}
              >
                Sair
              </Button>
            ) : null}
          </div>
        </header>

        <div className="evolua-shell__body">
          <aside className="evolua-shell__side" aria-label="Menu principal">
            <NavLink
              to="/"
              className="evolua-shell__side-mark"
              aria-label="Evolua HCM"
              {...(collapsed ? {} : { tabIndex: -1, "aria-hidden": true })}
            >
              <BrandLogo size="mark" alt="" />
            </NavLink>
            <nav className="evolua-shell__nav">
              {grouped.map((group) => (
                <div key={group.id} className="evolua-shell__group">
                  <p className="evolua-shell__group-label">{group.label}</p>
                  {group.items.map((item) => (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      end={item.path === "/"}
                      className="evolua-shell__nav-link"
                      title={item.label}
                    >
                      <Icon name={asIcon(item.icon)} size={18} />
                      <span className="evolua-shell__nav-text">{item.label}</span>
                      {item.badge ? <Badge tone="warning">{item.badge}</Badge> : null}
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
            <button
              type="button"
              className="evolua-shell__collapse"
              onClick={() => setCollapsed((v) => !v)}
              aria-pressed={collapsed}
              aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
            >
              <Icon name={collapsed ? "chevron-right" : "chevron-left"} size={16} />
              <span className="evolua-shell__nav-text">Recolher</span>
            </button>
          </aside>
          <main className="evolua-shell__main" id="conteudo-principal">
            <Outlet />
          </main>
        </div>

        <nav className="evolua-shell__bottom" aria-label="Navegação mobile">
          {bottomItems.map((item) => (
            <NavLink
              key={item.id}
              to={item.path}
              end={item.path === "/"}
              className="evolua-shell__bottom-link"
            >
              <Icon name={asIcon(item.icon)} size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
          {moreItems.length > 0 ? (
            <button
              type="button"
              className="evolua-shell__bottom-link"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen(true)}
            >
              <Icon name="more" size={20} />
              <span>Mais</span>
            </button>
          ) : null}
        </nav>
        <Modal open={moreOpen} title="Menu" onClose={() => setMoreOpen(false)}>
          <nav className="evolua-shell__more-nav" aria-label="Mais destinos">
            {moreGrouped.map((group) => (
              <div key={group.id} className="evolua-shell__more-group">
                <p className="evolua-shell__group-label">{group.label}</p>
                {group.items.map((item) => (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    end={item.path === "/"}
                    className="evolua-shell__more-link"
                    onClick={() => setMoreOpen(false)}
                  >
                    <Icon name={asIcon(item.icon)} size={18} />
                    <span>{item.label}</span>
                    {item.badge ? <Badge tone="warning">{item.badge}</Badge> : null}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        </Modal>
      </div>
    </ToastProvider>
  );
}
